import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Typography as MuiTypography,
  InputAdornment,
  Tooltip,
  ClickAwayListener,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { PageHeader, Typography } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import dayjs from 'dayjs';
import { invoiceService, type InvoiceItem } from '@/services/invoiceService';
import { FilterPanel } from '../../components/InvoiceFilters/FilterPanel';
import type { InvoiceFilters } from '../../components/InvoiceFilters/FilterPanel';
import { formatCurrency } from '@/utils/formatters';

const statusOptions = ['INITIATED', 'PAID', 'HOLD', 'REJECTED'];

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [searchKey, setSearchKey] = useState<'invoice_number' | 'merchant_uuid'>('invoice_number');
  const [searchValue, setSearchValue] = useState('');

  const [filters, setFilters] = useState<InvoiceFilters>({ statusFilter: '' });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleFilterPanelOpen = () => setIsFilterPanelOpen(true);
  const handleFilterPanelClose = () => setIsFilterPanelOpen(false);
  const handleFiltersChange = (next: InvoiceFilters) => setFilters(next);
  const handleClearAllFilters = () => {
    setFilters({ statusFilter: '', dateRange: undefined });
    setStartDate('');
    setEndDate('');
    setStatus('');
    fetchInvoices();
  };
  const handleApplyFilters = () => {
    const statuses = filters.statusFilter.split(',').filter(Boolean);
    setStatus(statuses.length === 1 ? statuses[0] : '');
    if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
      const start = filters.dateRange.startDate.split('T')[0];
      const end = filters.dateRange.endDate.split('T')[0];
      setStartDate(start);
      setEndDate(end);
    } else {
      setStartDate('');
      setEndDate('');
    }
    setIsFilterPanelOpen(false);
    fetchInvoices();
  };

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(i =>
        (i.invoice_number || '').toLowerCase().includes(s) ||
        (i.payer_name || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [invoices, search]);

  const paginatedInvoices = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredInvoices.slice(start, end);
  }, [filteredInvoices, page, rowsPerPage]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (startDate) params.date_from = startDate;
      if (endDate) params.date_to = endDate;
      if (status) params.invoice_status = status;
      if (searchValue.trim()) params[searchKey] = searchValue.trim();
      const res = await invoiceService.list(params);

      const data = res?.results?.data || res.data || [];
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, status, searchValue]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = async (invoice: InvoiceItem) => {
    try {
      const invoiceId = (invoice as any).invoice_id || (invoice.metadata as any)?.invoice_id;
      if (!invoiceId) {
        console.error('Invoice ID not found');
        return;
      }

      const response = await invoiceService.download(String(invoiceId));
      if (response.success && response.download_link) {
        window.open(response.download_link, '_blank');
      }
    } catch (e) {
      console.error('Failed to download invoice:', e);
    }
  };

  const handleViewTransactions = (invoice: InvoiceItem) => {
    setSelectedInvoice(invoice);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedInvoice(null);
  };

  const actions: PageHeaderAction[] = [
    {
      id: 'refresh',
      label: 'Refresh',
      onClick: fetchInvoices,
    },
  ];

  const subtitle = `${filteredInvoices.length} ${filteredInvoices.length === 1 ? 'invoice' : 'invoices'}`;

  return (
    <Box>
      <PageHeader title="Invoices" subtitle={subtitle} />

      {/* Top Controls styled similar to Payments page */}
      <Box
        mb={3}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 2, md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
            <Select value={searchKey} onChange={e => setSearchKey(e.target.value as any)}>
              <MenuItem value="invoice_number">Invoice Number</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            type="search"
            placeholder="Search"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchInvoices()}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />

          <Button size="small" onClick={handleClearAllFilters}>Clear Filters</Button>
        </Box>

        {/* <Box display="flex" gap={1} alignItems="center">
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={fetchInvoices} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
          <Button variant="outlined" size="small" startIcon={<ExportIcon />} sx={{ textTransform: 'none' }}>
            Export
          </Button>
        </Box> */}
      </Box>

      <Button
        ref={filterButtonRef}
        variant="outlined"
        size="small"
        onClick={handleFilterPanelOpen}
        sx={{ textTransform: 'none', mb: 2 }}
      >
        Add filters
      </Button>

      {isFilterPanelOpen && (
        <ClickAwayListener onClickAway={handleFilterPanelClose}>
          <Box>
            <FilterPanel
              open={isFilterPanelOpen}
              anchorEl={filterButtonRef.current}
              onClose={handleFilterPanelClose}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearAllFilters}
            />
          </Box>
        </ClickAwayListener>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <MuiTypography color="error">{error}</MuiTypography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Invoice No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Note</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Initiated</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Settled</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Expiry</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Payment Mode</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>UTR</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bank</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>IFSC</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Payer</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedInvoices.map((inv, idx) => (
                    <TableRow key={(inv.invoice_number || '') + idx}>
                      <TableCell sx={{ width: '200px' }}>{inv.invoice_number}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          {formatCurrency(Number(inv.invoice_amount))}
                        </Typography>
                      </TableCell>
                      <TableCell>{inv.invoice_note || '—'}</TableCell>
                      <TableCell>
                        <Chip label={inv.invoice_status} size="small" />
                      </TableCell>
                      <TableCell>{inv.invoice_initiated_date ? dayjs(inv.invoice_initiated_date).format('DD MMM YYYY') : '—'}</TableCell>
                      <TableCell>{inv.invoice_settled_date ? dayjs(inv.invoice_settled_date).format('DD MMM YYYY') : '—'}</TableCell>
                      <TableCell>{inv.invoice_expiry_date ? dayjs(inv.invoice_expiry_date).format('DD MMM YYYY') : '—'}</TableCell>
                      <TableCell>{inv.mode_of_payment || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{inv.utr || '—'}</TableCell>
                      <TableCell>{inv.bank_name || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{inv.ifsc || '—'}</TableCell>
                      <TableCell>{inv.payer_name || '—'}</TableCell>
                      <TableCell align="right">


                        <Box sx={{ display: 'flex', gap: 1 }}>


                          <Tooltip title="Transation Details">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleViewTransactions(inv)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownload(inv)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedInvoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        <MuiTypography>No invoices found</MuiTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredInvoices.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>

      <Drawer anchor="right" open={isDrawerOpen} onClose={handleCloseDrawer}>
        <Box sx={{ width: 600, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <MuiTypography variant="h6">Transaction Details</MuiTypography>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {selectedInvoice && (
            <Box>

              {(selectedInvoice as any).transaction_invoice && (selectedInvoice as any).transaction_invoice.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedInvoice as any).transaction_invoice.map((transaction: any, index: number) => (
                        <TableRow key={transaction.transaction_id || index}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{transaction.transaction_id}</TableCell>
                          <TableCell>{formatCurrency(Number(transaction.transaction_amount))}</TableCell>
                          <TableCell>{dayjs(transaction.transaction_date).format('DD MMM YYYY, HH:mm')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <MuiTypography color="text.secondary">No transactions found</MuiTypography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default InvoicesPage;
